import { useState } from "react";
import { DateField, Modal, SubcategoryField, TextField } from "./AdministrationFields";
import { PatientDocumentPanel } from "./PatientDocuments";
import { formatPatientName } from "../model/administrationRules";
import {
    confirmDeleteButtonStyle,
    deletePatientButtonStyle,
    errorStyle,
    identityModalActionsStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "../styles/administrationModalStyles";
import {
    labelStyle,
    notInvoicedCheckboxStyle,
    statusCheckboxStyle,
    statusControlsStyle,
    verticalFieldLabelStyle,
} from "../styles/administrationStyles";

function ConfirmPatientDeletion({ row, deleting, zIndex, onCancel, onConfirm }) {
    return (
        <Modal title="Supprimer le patient ?" onClose={() => !deleting && onCancel()} width={480} zIndex={zIndex}>
            <div style={{ display: "grid", gap: "18px" }}>
                <p style={{ margin: 0, color: "#374151", lineHeight: 1.6 }}>
                    Êtes-vous sûr de vouloir supprimer <strong>{formatPatientName(row)}</strong> et tous ses produits ?
                    Cette action est irréversible.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                    <button type="button" onClick={onCancel} disabled={deleting} style={secondaryButtonStyle}>
                        Annuler
                    </button>
                    <button type="button" onClick={onConfirm} disabled={deleting} style={confirmDeleteButtonStyle}>
                        {deleting ? "Suppression…" : "Supprimer définitivement"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export function PatientIdentityModal({
    row,
    productionSubcategories,
    onClose,
    onSave,
    onDelete,
    onUploadDocument,
    onOpenDocument,
    onDeleteDocument,
    documentBusy = false,
    modalZIndex,
}) {
    const [form, setForm] = useState({
        nom: row.nom || "",
        prenom: row.prenom || "",
        dateNaissance: row.dateNaissance || "",
        chambre: row.chambre || "",
        productionSubcategory: row.productionSubcategory || "",
        nir: row.nir || "",
        notInvoiced: Boolean(row.notInvoiced),
    });
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState("");
    const canManageDocuments = Boolean(onUploadDocument && onOpenDocument && onDeleteDocument);

    const submit = async event => {
        event.preventDefault();
        if (!form.nom.trim() || !form.prenom.trim()) {
            setError("Le nom et le prénom sont obligatoires.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            await onSave({
                nom: form.nom.trim(),
                prenom: form.prenom.trim(),
                dateNaissance: form.dateNaissance || null,
                chambre: form.chambre.trim() || null,
                productionSubcategory: form.productionSubcategory || null,
                nir: form.nir.trim() || null,
                notInvoiced: form.notInvoiced,
            });
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible de modifier l'identité du résident.");
        } finally {
            setSaving(false);
        }
    };

    const deletePatient = async () => {
        setDeleting(true);
        setError("");
        try {
            await onDelete();
            onClose();
        } catch (requestError) {
            setError(requestError.response?.data?.message || "Impossible de supprimer ce patient.");
            setConfirmDelete(false);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Modal title={`Modifier le résident — ${formatPatientName(row)}`} onClose={onClose} width={520} zIndex={modalZIndex}>
                <form onSubmit={submit} style={{ display: "grid", gap: "14px" }}>
                    {error && <p role="alert" style={errorStyle}>{error}</p>}
                    <label style={labelStyle}>Nom<TextField value={form.nom} onChange={value => setForm(current => ({ ...current, nom: value }))} /></label>
                    <label style={labelStyle}>Prénom<TextField value={form.prenom} onChange={value => setForm(current => ({ ...current, prenom: value }))} /></label>
                    <label style={labelStyle}>Date de naissance<DateField value={form.dateNaissance} onChange={value => setForm(current => ({ ...current, dateNaissance: value }))} /></label>
                    <label style={labelStyle}>Chambre<TextField value={form.chambre} onChange={value => setForm(current => ({ ...current, chambre: value }))} /></label>
                    <label style={labelStyle}>NIR<TextField value={form.nir} onChange={value => setForm(current => ({ ...current, nir: value }))} /></label>
                    <label style={labelStyle}>
                        Sous-catégorie (planning, production, etc.) (facultatif)
                        <SubcategoryField
                            value={form.productionSubcategory}
                            options={productionSubcategories}
                            onChange={value => setForm(current => ({ ...current, productionSubcategory: value || "" }))}
                        />
                    </label>
                    <div style={statusControlsStyle}>
                        <label style={notInvoicedCheckboxStyle(form.notInvoiced)}>
                            <input
                                type="checkbox"
                                aria-label="Pas facturé"
                                checked={form.notInvoiced}
                                onChange={event => setForm(current => ({
                                    ...current,
                                    notInvoiced: event.target.checked,
                                }))}
                                style={statusCheckboxStyle}
                            />
                            <span>
                                <strong style={{ display: "block" }}>Pas facturé</strong>
                                <small style={{ color: "#6b7280", fontWeight: 500 }}>
                                    La ligne apparaîtra sur fond rouge
                                </small>
                            </span>
                        </label>
                    </div>
                    {canManageDocuments && (
                        <section style={{ display: "grid", gap: "8px" }}>
                            <span style={verticalFieldLabelStyle}>Documents patient</span>
                            <PatientDocumentPanel
                                documents={row.documents || []}
                                busy={documentBusy}
                                uploadEnabled
                                onUpload={onUploadDocument}
                                onOpen={onOpenDocument}
                                onDelete={onDeleteDocument}
                            />
                        </section>
                    )}
                    <div style={identityModalActionsStyle}>
                        <button
                            type="button"
                            onClick={() => setConfirmDelete(true)}
                            disabled={saving}
                            style={deletePatientButtonStyle}
                        >
                            Supprimer le patient
                        </button>
                        <div style={{ display: "flex", gap: "10px" }}>
                            <button type="button" onClick={onClose} disabled={saving} style={secondaryButtonStyle}>Annuler</button>
                            <button type="submit" disabled={saving} style={primaryButtonStyle}>
                                {saving ? "Enregistrement…" : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </form>
            </Modal>
            {confirmDelete && (
                <ConfirmPatientDeletion
                    row={row}
                    deleting={deleting}
                    zIndex={modalZIndex}
                    onCancel={() => setConfirmDelete(false)}
                    onConfirm={deletePatient}
                />
            )}
        </>
    );
}
